import { primitive, semantic } from "@oshimap/design-tokens";
import { stableRotation } from "@oshimap/utils";
import type { ShareCardDto } from "@oshimap/schemas";

/**
 * 공유 카드 그리기 (OD-014).
 *
 * 들어가는 것: OshiMap 브랜딩 + 내 집계 + 내가 켠 항목. 그게 전부다.
 * 들어가지 않는 것: 남의 사진, 저작권 이미지, 내가 끈 항목 (NFR-PV-05).
 *
 * 색은 디자인 토큰에서 읽는다. 카드 색만 따로 적어 두면 팔레트를 바꿀 때 여기만 남는다.
 */
export interface ShareOptions {
  /** 이름을 뺄 수 있다 — 집계만 자랑하고 싶은 사람이 있다. */
  name: boolean;
  places: boolean;
  cities: boolean;
  stamps: boolean;
}

export const DEFAULT_SHARE_OPTIONS: ShareOptions = { name: true, places: true, cities: true, stamps: true };

export const CARD = { w: 1080, h: 1350 };

const INK = primitive.neutral[900];
const SUB = primitive.neutral[500];
const PURPLE = primitive.purple[500];
const PURPLE_DEEP = primitive.purple[700];
const PINK = primitive.pink[400];
const YELLOW = primitive.yellow[300];
const CANVAS = semantic["bg.canvas"];
const SURFACE = primitive.neutral[50];
const BORDER = primitive.neutral[200];

const FONT = `-apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Pretendard", "Noto Sans KR", system-ui, sans-serif`;

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, opts: {
  size: number; weight?: number; color?: string; align?: CanvasTextAlign; tracking?: number;
}) {
  g.save();
  g.font = `${opts.weight ?? 500} ${opts.size}px ${FONT}`;
  g.fillStyle = opts.color ?? INK;
  g.textAlign = opts.align ?? "left";
  g.textBaseline = "alphabetic";
  if (opts.tracking) {
    /* letterSpacing 은 아직 없는 브라우저가 있어 직접 벌린다. */
    const chars = [...s];
    const total = chars.reduce((n, c) => n + g.measureText(c).width, 0) + opts.tracking * (chars.length - 1);
    let cx = opts.align === "center" ? x - total / 2 : opts.align === "right" ? x - total : x;
    for (const c of chars) { g.fillText(c, cx, y); cx += g.measureText(c).width + opts.tracking; }
  } else {
    g.fillText(s, x, y);
  }
  g.restore();
}

/** 스탬프 하나. 각도는 고정 시드라 같은 스탬프는 늘 같은 각으로 기운다. */
function stamp(g: CanvasRenderingContext2D, label: string, cx: number, cy: number, r: number, seed: string) {
  g.save();
  g.translate(cx, cy);
  g.rotate((stableRotation(seed, 7) * Math.PI) / 180);

  g.fillStyle = SURFACE;
  g.strokeStyle = PURPLE;
  g.lineWidth = 5;
  g.setLineDash([13, 9]);
  g.beginPath();
  g.arc(0, 0, r, 0, Math.PI * 2);
  g.fill();
  g.stroke();
  g.setLineDash([]);

  /* 이름이 길면 두 줄로 나눠 담는다. 잘라내면 무엇을 받았는지 알 수 없다. */
  const words = label.split(/\s+/);
  const lines: string[] = [];
  g.font = `700 ${Math.round(r * 0.30)}px ${FONT}`;
  let line = "";
  for (const wd of words) {
    const next = line ? `${line} ${wd}` : wd;
    if (g.measureText(next).width > r * 1.5 && line) { lines.push(line); line = wd; }
    else line = next;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, 2);

  const size = Math.round(r * (shown.length > 1 ? 0.26 : 0.30));
  const lh = size * 1.2;
  const top = -((shown.length - 1) * lh) / 2;
  shown.forEach((l, i) => {
    text(g, l, 0, top + i * lh + size * 0.36, { size, weight: 700, color: PURPLE_DEEP, align: "center" });
  });

  g.restore();
}

export function drawShareCard(
  g: CanvasRenderingContext2D,
  data: ShareCardDto,
  opts: ShareOptions = DEFAULT_SHARE_OPTIONS,
): void {
  const { w, h } = CARD;
  const pad = 88;

  g.clearRect(0, 0, w, h);
  g.fillStyle = CANVAS;
  g.fillRect(0, 0, w, h);

  /* 여권 표지처럼 위쪽에 색 띠 하나. 장식은 한 군데만 쓴다. */
  g.fillStyle = PURPLE;
  g.fillRect(0, 0, w, 18);
  g.fillStyle = PINK;
  g.fillRect(0, 18, w * 0.34, 8);
  g.fillStyle = YELLOW;
  g.fillRect(w * 0.34, 18, w * 0.16, 8);

  let y = pad + 96;

  text(g, "덕질 여권", pad, y, { size: 76, weight: 700, color: INK });
  y += 62;
  text(g, data.logoText, pad, y, { size: 30, weight: 700, color: PURPLE, tracking: 6 });

  if (opts.name) {
    y += 92;
    text(g, data.displayName, pad, y, { size: 46, weight: 600, color: INK });
  }

  /* 숫자 — 이 카드의 본론 */
  if (opts.places) {
    y += 132;
    const stats: Array<[number, string]> = [
      [data.totalPlaces, "다녀온 곳"],
      [data.totalEvents, "이벤트"],
      [data.cities.length, "지역"],
    ];
    const colW = (w - pad * 2) / stats.length;
    stats.forEach(([n, label], i) => {
      const cx = pad + colW * i;
      text(g, String(n), cx, y, { size: 96, weight: 700, color: PURPLE_DEEP });
      text(g, label, cx, y + 44, { size: 28, weight: 500, color: SUB });
    });
    y += 44;
  }

  /* 지역 */
  if (opts.cities && data.cities.length > 0) {
    y += 118;
    text(g, "많이 간 곳", pad, y, { size: 30, weight: 600, color: SUB });
    y += 58;
    let x = pad;
    for (const c of data.cities.slice(0, 3)) {
      const label = `${c.name} ${c.count}`;
      g.font = `600 34px ${FONT}`;
      const tw = g.measureText(label).width;
      const bw = tw + 56;
      if (x + bw > w - pad) break;
      g.fillStyle = SURFACE;
      g.strokeStyle = BORDER;
      g.lineWidth = 2;
      roundRect(g, x, y - 44, bw, 66, 33);
      g.fill();
      g.stroke();
      text(g, label, x + 28, y, { size: 34, weight: 600, color: INK });
      x += bw + 18;
    }
    y += 30;
  }

  /* 스탬프 */
  if (opts.stamps && data.stamps.length > 0) {
    y += 108;
    text(g, "받은 스탬프", pad, y, { size: 30, weight: 600, color: SUB });
    y += 42;
    const shown = data.stamps.slice(0, 4);
    const r = 92;
    const gap = (w - pad * 2 - shown.length * r * 2) / Math.max(shown.length - 1, 1);
    shown.forEach((s, i) => {
      stamp(g, s.name, pad + r + i * (r * 2 + gap), y + r + 10, r, s.id);
    });
  }

  /* 바닥 — 어디서 만든 카드인지 */
  g.fillStyle = BORDER;
  g.fillRect(pad, h - 156, w - pad * 2, 2);
  text(g, "oshimap", pad, h - 92, { size: 32, weight: 700, color: PURPLE, tracking: 4 });
  text(g, "덕질하러 갈 곳을 찾고, 다녀온 곳을 모읍니다", w - pad, h - 92,
    { size: 26, weight: 500, color: SUB, align: "right" });
}
